import { Tooltip } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import CommonImage from '@components/CommonImage';
import CustomDropdown from '@components/CustomDropdown';
import ModalContainer from '@components/modals/ModalContainer';
import { toastSuccess } from '@components/Toast';

import { incoTerms } from '@features/order/utils/projectUtil';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import {
	fetchEnumValues,
	fetchUserByEmail,
	findAllUsersByUserTypes,
	uploadDocument
} from '@services/commonService';
import { fetchBrandWithSubBrand, placeOrder } from '@services/orderService';

import { getShortName, onUploadFile, printApiError, repeatTag } from '@utils/Util';

import { useGetProjectDealListQuery } from '@/features/sampleDevelopment/api/sampleDevelopmentApi';
import { formatProjectDealOptions } from '@/features/sampleDevelopment/utils';

// import { groupBy } from '@features/favourites/utils/favouriteUtil';

const CreateOrderModal = ({ onClose, brandId, collectionId, folderId, isShowRepeatOrderTag = false }) => {
	const dispatch = useDispatch();
	let navigate = useNavigate();
	const [formData, setFormData] = useState({
		orderTitle: '',
		season: '',
		incotermsLocation: '',
		projectManagerId: null,
		accountManagerId: null,
		buyerId: null,
		brandId: null,
		subBrandId: null,
		stream: null,
		poNumber: null,
		startDate: null,
		dueDate: null,
		projectDealName: null
	});
	const [errors, setErrors] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [projectManagers, setProjectManagers] = useState([]);
	const [accountManagers, setAccountManagers] = useState([]);
	const [buyerList, setBuyerList] = useState([]);
	const [salesChannels, setSalesChannels] = useState([]);
	const [subBrands, setSubBrands] = useState([]);
	const [isSubBrandCleared, setIsSubBrandCleared] = useState(false);
	const [brandName, setBrandName] = useState('');
	const [, setStartDate] = useState('');
	const [, setDueDate] = useState('');
	const [poDocIdList, setPoDocIdList] = useState([]);
	const [selectedIncoTerm, setSelectedIncoTerm] = useState('');
	const selectedProductIds = useSelector((store) => store.product.selectedProductIds);

	const { data: projectDealList } = useGetProjectDealListQuery(
		{
			params: {
				brandId: formData?.brandId
			}
		},
		{
			skip: !formData?.brandId
		}
	);

	const validateForm = () => {
		const newErrors = {};
		if (!formData.orderTitle.trim()) newErrors.orderTitle = 'Order title is required.';
		if (!formData.projectManagerId) newErrors.projectManager = 'Project Manager is required.';
		if (!formData.accountManagerId) newErrors.accountManager = 'Account Manager is required.';
		if (!formData.buyerId) newErrors.buyer = 'Buyer is required.';
		if (!formData.dueDate) newErrors.dueDate = 'ETD is required.';
		if (!formData.incotermsLocation) newErrors.incoTermLocation = 'Location is required.';
		if (!selectedIncoTerm?.value) newErrors.incoTerm = 'Incoterm is required.';
		if (!formData.stream) newErrors.stream = 'Revenue stream is required.';
		return newErrors;
	};

	const handleSubmitInfo = async () => {
		const validationErrors = validateForm();
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			return;
		}

		setErrors({});
		const commonFields = {
			poNumber: formData.poNumber, // Replace with dynamic value if needed
			poReceiveDate: formData.startDate, // Ensure `startDate` is defined or replace with dynamic value
			deliveryDate: formData.dueDate // Ensure `etd` is defined or replace with dynamic value
		};
		const productWiseQuantityPriceList = selectedProductIds.map((productId) => ({
			productId,
			...commonFields
		}));
		const selectedSubBrandId = isSubBrandCleared
			? null
			: subBrands.length === 1
				? subBrands[0].id
				: formData.subBrandId;
		let body = {
			brandId,
			dueDate: formData.dueDate,
			startDate: formData.startDate,
			title: formData.orderTitle,
			poNumber: formData.poNumber,
			productWiseQuantityPriceList,
			poDocIdList: poDocIdList.map((item) => item.id),
			projectManagerId: formData.projectManagerId,
			accountManagerId: formData.accountManagerId,
			buyerId: formData.buyerId,
			stream: formData.stream,
			season: formData.season,
			incotermsLocation: formData.incotermsLocation,
			incoterms: selectedIncoTerm?.value || '',
			dealId: formData?.projectDealName?.dealId || null
		};
		if (selectedSubBrandId !== null && selectedSubBrandId !== undefined) {
			body.subBrandId = selectedSubBrandId;
		}

		if (collectionId) {
			body.collectionId = collectionId;
		}
		if (folderId) {
			body.folderId = folderId;
		}

		try {
			setIsSubmitting(true);
			dispatch(openLoader());
			const response = await placeOrder(body);
			if (response?.success) {
				setErrors([]);
				toastSuccess('Order Created Successful!');
				navigate(`/orders/view/${response?.id}`);
			}
		} catch (error) {
			printApiError(error);
		} finally {
			setIsSubmitting(false);
			dispatch(closeLoader());
		}
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};
	const handleSelectChange = (e, formField) => {
		if (formField === 'subBrandId' && e?.id === 'CLEAR_SUB_BRAND') {
			setIsSubBrandCleared(true);
			setFormData((prev) => ({ ...prev, subBrandId: null }));
			return;
		}
		if (formField === 'subBrandId') {
			setIsSubBrandCleared(false);
		}
		const { id } = e;
		setFormData((prev) => ({ ...prev, [formField]: id }));
	};

	const handleDealChange = (value) => {
		setFormData((prev) => ({ ...prev, projectDealName: value }));
		setErrors((prev) => ({ ...prev, projectDealName: '' }));
	};
	const getItemName = (items, key) => {
		if (key === 'subBrandId' && isSubBrandCleared) {
			return '';
		}
		if (items.length === 1 && key === 'subBrandId') {
			return items[0].value;
		}
		return items?.find((p) => p.id === formData?.[key])?.value;
		//return items?.find((p) => p.id === formData?.[key])?.value;
	};

	const onPoReceiveDateChange = (e) => {
		setStartDate(e.target.value);
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};
	const onDueDateChange = (e) => {
		setDueDate(e.target.value);
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	useEffect(() => {
		(async () => {
			try {
				const salesFunnels = await fetchEnumValues('info.nitex.app.common.enums.OrderStream');
				const data = await findAllUsersByUserTypes(
					`?userTypes=PROJECT_MANAGER&userTypes=ACCOUNT_MANAGER`
				);
				setSalesChannels(salesFunnels?.map((item) => ({ id: item.constant, value: item.name })));
				setProjectManagers(data.PROJECT_MANAGER?.map((item) => ({ id: item.id, value: item.name })));
				setAccountManagers(data.ACCOUNT_MANAGER?.map((item) => ({ id: item.id, value: item.name })));
			} catch {}
		})();
	}, []);

	useEffect(() => {
		(async () => {
			try {
				let params = `?organizationType=BUYER&isNitexUser=${false}&brandId=${brandId}&page=0&size=100`;
				const data = await fetchUserByEmail(params);
				setBuyerList(data?.map((item) => ({ id: item.id, value: item.email })));
			} catch {}
		})();
	}, []);

	useEffect(() => {
		(async () => {
			try {
				let params = `${brandId}/brands-with-sub-brands`;
				const data = await fetchBrandWithSubBrand(params);
				setFormData((prev) => ({ ...prev, brandId: data?.brand.id }));
				setBrandName(data?.brand?.name);
				setSubBrands(data?.subBrands?.map((item) => ({ id: item.id, value: item.name })));
				setIsSubBrandCleared(false);
				//setBuyerList(data?.map((item) => ({ label: item.id, value: item.email })));
			} catch {}
		})();
	}, []);

	const onUploadPo = async (poData) => {
		try {
			dispatch(openLoader());
			const data = await uploadDocument(poData);
			if (data) {
				setPoDocIdList((prev) => [...prev, { name: poData.name, id: data.id, docUrl: data.docUrl }]);
				toastSuccess('Uploaded successfully');
			}
			dispatch(closeLoader());
		} catch {
			dispatch(closeLoader());
		}
	};
	const onRemovePo = async (itemId) => {
		const updatedList = poDocIdList.filter((item) => item.id !== itemId);
		setPoDocIdList(updatedList);
	};
	return (
		<ModalContainer className="create-order-modal" onCloseModal={() => {}}>
			<div className="common-popup-body p-0">
				<div className="popup-wrapper">
					<div className="common-popup create-order-modal">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0">
									Create order {isShowRepeatOrderTag && repeatTag()}
								</h3>
							</div>
							<div className="close-btn">
								<CommonImage
									src="/icons/close.svg"
									width="24"
									alt="close"
									onClick={onClose}
								/>
							</div>
						</div>
						<div className="common-popup-body scroll-y-label">
							<form>
								<div className="create-order-form">
									<div className="row">
										<div className="col-md-4 pr-1">
											<div className="form-group">
												<label htmlFor="collection">Order title*</label>
												<input
													type="text"
													className="w-100"
													placeholder="Order Title"
													name="orderTitle"
													value={formData.orderTitle}
													onChange={handleInputChange}
												/>

												{errors.orderTitle && (
													<span className="error">{errors.orderTitle}</span>
												)}
											</div>
										</div>
										<div className="col-md-4 pl-1 pr-1">
											<div className="form-group">
												<div className="select-component">
													<label htmlFor="collection">Project Manager*</label>
													<CustomDropdown
														title="Select"
														items={projectManagers}
														onItemClick={(item) => {
															handleSelectChange(item, 'projectManagerId');
														}}
														selectedItem={getItemName(
															projectManagers,
															'projectManagerId'
														)}
														toolTipLength={35}
													/>
													{errors.projectManager && (
														<span className="error">{errors.projectManager}</span>
													)}
												</div>
											</div>
										</div>
										<div className="col-md-4 pl-1">
											<div className="form-group">
												<div className="select-component">
													<label htmlFor="collection">Account Manager*</label>
													<CustomDropdown
														title="Select"
														items={accountManagers}
														onItemClick={(item) => {
															handleSelectChange(item, 'accountManagerId');
														}}
														selectedItem={getItemName(
															accountManagers,
															'accountManagerId'
														)}
														toolTipLength={35}
													/>
													{errors.accountManager && (
														<span className="error">{errors.accountManager}</span>
													)}
												</div>
											</div>
										</div>
										<div className="col-md-4 pr-1">
											<div className="form-group">
												<div className="select-component">
													<label htmlFor="collection">Select buyer*</label>
													<CustomDropdown
														title="Select"
														items={buyerList}
														onItemClick={(item) => {
															handleSelectChange(item, 'buyerId');
														}}
														selectedItem={getItemName(buyerList, 'buyerId')}
														toolTipLength={35}
													/>
													{errors.buyer && (
														<span className="error">{errors.buyer}</span>
													)}
												</div>
											</div>
										</div>
										<div className="col-md-4 pl-1 pr-1">
											<div className="row">
												<div className="col-md-6 pr-1">
													<div className="form-group">
														<label htmlFor="collection">Season</label>
														<input
															type="text"
															className="w-100"
															placeholder="Season"
															name="season"
															value={formData.season}
															onChange={handleInputChange}
														/>
													</div>
												</div>
												<div className="col-md-6 pl-1">
													<div className="form-group">
														<div className="select-component">
															<label htmlFor="collection">Incoterm*</label>
															<CustomDropdown
																title="Select"
																items={incoTerms}
																onItemClick={(selected) => {
																	setSelectedIncoTerm({
																		id: selected.id,
																		value: selected.value
																	});
																}}
																selectedItem={selectedIncoTerm.value}
																toolTipLength={35}
															/>
															{errors.incoTerm && (
																<span className="error">
																	{errors.incoTerm}
																</span>
															)}
														</div>
													</div>
												</div>
											</div>
										</div>
										<div className="col-md-4 pl-1">
											<div className="form-group">
												<label htmlFor="collection">Incoterm Location*</label>
												<input
													type="text"
													className="w-100"
													placeholder="Incoterm Location"
													name="incotermsLocation"
													value={formData.incotermsLocation}
													onChange={handleInputChange}
												/>
												{errors.incoTermLocation && (
													<span className="error">{errors.incoTermLocation}</span>
												)}
											</div>
										</div>
										<div className="col-md-4 pr-1">
											<div className="row">
												<div className="col-md-6 pr-1">
													<div className="form-group">
														<div className="select-component">
															<label htmlFor="collection">Brand</label>
															<input
																type="text"
																className="w-100"
																placeholder="Brand"
																name="brand"
																value={brandName}
																disabled
															/>
														</div>
													</div>
												</div>
												<div className="col-md-6 pl-1 pr-1">
													<div className="form-group">
														<div className="select-component">
															<label htmlFor="collection">Sub-brand</label>
															<CustomDropdown
																title="Select"
																items={
																	subBrands?.length > 0
																		? [
																				{
																					id: 'CLEAR_SUB_BRAND',
																					value: 'Clear Sub-brand'
																				},
																				...subBrands
																			]
																		: []
																}
																onItemClick={(item) => {
																	handleSelectChange(item, 'subBrandId');
																}}
																selectedItem={getItemName(
																	subBrands,
																	'subBrandId'
																)}
																toolTipLength={35}
																clearLabel="Clear Sub-brand"
															/>
														</div>
													</div>
												</div>
											</div>
										</div>
										<div className="col-md-4 pr-1">
											<div className="form-group">
												<label htmlFor="po-number">Deal*</label>
												<CustomDropdown
													title="Select"
													onItemClick={(value) => handleDealChange(value)}
													selectedItem={formData?.projectDealName?.dealName}
													items={formatProjectDealOptions(projectDealList)}
													toolTipLength={25}
												/>
												{errors?.projectDealName && (
													<p className="error">{errors?.projectDealName}</p>
												)}
											</div>
										</div>
										<div className="col-md-4 pl-1">
											<div className="form-group">
												<div className="select-component">
													<label htmlFor="collection">Revenue stream*</label>
													<CustomDropdown
														title="Select"
														items={salesChannels}
														onItemClick={(item) => {
															handleSelectChange(item, 'stream');
														}}
														selectedItem={getItemName(salesChannels, 'stream')}
														toolTipLength={35}
													/>
													{errors.stream && (
														<span className="error">{errors.stream}</span>
													)}
												</div>
											</div>
										</div>
										<div className="col-md-4 pr-1">
											<div className="form-group">
												<label htmlFor="collection">PO number</label>
												<input
													type="text"
													className="w-100"
													placeholder="PO number"
													name="poNumber"
													value={formData.poNumber}
													onChange={handleInputChange}
												/>
											</div>
										</div>
										<div className="col-md-4 pr-1 pl-1">
											<div className="form-group">
												<label htmlFor="collection">PO receive date</label>
												<input
													type="date"
													id="po-receive"
													name="startDate"
													value={formData?.startDate}
													onChange={onPoReceiveDateChange}
												/>
											</div>
										</div>
										<div className="col-md-4 pl-1">
											<div className="form-group">
												<label htmlFor="collection">
													ETD (Estimated Time of Delivery)*
												</label>
												<input
													type="date"
													id="etd"
													name="dueDate"
													value={formData?.dueDate}
													onChange={onDueDateChange}
												/>
												{errors.dueDate && (
													<span className="error">{errors.dueDate}</span>
												)}
											</div>
										</div>
									</div>
								</div>
								<div className="create-order-form po-upload-and-file">
									<div className="row">
										<div className="col-md-12 pr-2">
											<div className="upload-img mb-2">
												<div className="file-uploader height-small upload-design-image mb-4 mb-sm-0">
													<div className="d-flex justify-content-start align-items-center">
														<label
															htmlFor="drag-upload"
															className="drag-upload d-flex justify-content-center align-items-center"
														>
															<CommonImage
																src={'/icons/upload-blue.svg'}
																alt={'file image'}
																width="24"
															/>
															<p className="ml-1">Upload PO</p>
														</label>
														{poDocIdList?.length === 0 && (
															<p className="regular-14 gray_dark_02 pl-4">
																No PO uploaded
															</p>
														)}
													</div>

													<input
														type="file"
														id="drag-upload"
														className="file-upload"
														name="po-upload"
														multiple
														accept="*"
														onChange={(e) =>
															onUploadFile(
																e,
																'PURCHASE_ORDER',
																onUploadPo,
																'MULTIPLE'
															)
														}
													/>
												</div>

												<div className="all-pdf-files mt-3 d-flex flex-wrap justify-content-start align-items-center">
													{poDocIdList?.map((item) => (
														<div
															key={item.id}
															className="single-pdf-file d-flex align-items-center mr-4 mb-2"
														>
															<CommonImage
																src={'/images/file-pdf.svg'}
																alt={'file image'}
																className="add-to-favorite"
																width="24"
															/>
															<Tooltip title={item.name} placement="top" arrow>
																<a
																	href={item.docUrl}
																	target="_blank"
																	rel="noopener noreferrer"
																>
																	{getShortName(item.name, 15)}
																</a>
															</Tooltip>
															<div className="acton-img cursor-pointer">
																<CommonImage
																	src={'/icons/delete_red.svg'}
																	alt={'delete'}
																	width="20"
																	onClick={() => onRemovePo(item.id)}
																/>
															</div>
														</div>
													))}
												</div>
											</div>
										</div>
									</div>
								</div>
							</form>
						</div>
						<div className="modal-footer">
							<div className="footer-buttons text-right">
								<button className="button outline size32 ml-2" onClick={onClose}>
									Cancel
								</button>
								<button
									className="button size32 ml-2"
									onClick={handleSubmitInfo}
									disabled={isSubmitting}
								>
									{isSubmitting ? (
										<>
											<span
												className="spinner-border spinner-border-sm mr-1"
												role="status"
												aria-hidden="true"
											/>
											Submitting...
										</>
									) : (
										'Submit'
									)}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
};

export default CreateOrderModal;
